/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosDefaults, CreateAxiosDefaults } from "axios";

import { toCamelCase } from "../../utils/toCamelCase";
import { toSnakeCase } from "../../utils/toSnakeCase";

type CustomAxiosClientConfig = {
  shouldCamelCaseResponse: boolean;
} & CreateAxiosDefaults;

export type CustomAxiosDefaults = {
  shouldCamelCaseResponse?: boolean;
} & AxiosDefaults<any>;

export const createAxiosClient = (
  baseURL: string,
  timeout = 10000,
  shouldCamelCaseResponse = false
) => {
  const client = axios.create({
    baseURL: baseURL,
    timeout,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    shouldCamelCaseResponse,
  } as CustomAxiosClientConfig);

  // Middleware goes here
  // Add a response interceptor and camelCase return data (for JS)
  client.interceptors.response.use(
    (response) => {
      if ((response.config as CustomAxiosClientConfig).shouldCamelCaseResponse === false)
        return response;

      response.data = toCamelCase(response.data);
      return response;
    },
    (error) => {
      // Check if error.response exists before accessing its properties
      if (!error.response) {
        error.response = { data: null };
        return Promise.reject(error);
      }

      // Check if shouldCamelCaseResponse is false
      if (error.response.config?.shouldCamelCaseResponse === false) {
        return Promise.reject(error);
      }

      // Transform the error response data to camelCase
      error.response.data = toCamelCase(error.response.data);
      return Promise.reject(error);
    }
  );

  // Add a request interceptor and snakeCase POST data (for django)
  client.interceptors.request.use(
    (config) => {
      if ((config as CustomAxiosClientConfig).shouldCamelCaseResponse === false) return config;

      config.params = toSnakeCase(config.params);
      config.data = toSnakeCase(config.data);
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return client;
};
